import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, Trash2, FileText, CreditCard, RefreshCw, Loader2, Ban, Check, X, Download, Mail } from "lucide-react";
import { toast } from "sonner";

const statusColors = {
  Active: "bg-green-50 text-green-700 border-green-200",
  "Fully Applied": "bg-blue-50 text-blue-700 border-blue-200",
  Refunded: "bg-purple-50 text-purple-700 border-purple-200",
  Cancelled: "bg-gray-100 text-gray-500 border-gray-200",
};
const creditTypes = ["Return", "Discount", "Error Correction", "Write-off", "Goodwill", "Other"];
const fmt = (v) => `R${(parseFloat(v) || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2 })}`;

export default function CreditNotesPage() {
  const { id } = useParams();
  if (id === "new") return <CreateCreditNote />;
  if (id) return <CreditNoteDetail cnId={id} />;
  return <CreditNoteList />;
}

function CreditNoteList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/credit-notes?status=${filter}&limit=50`).then(r => { setItems(r.data.credit_notes); setTotal(r.data.total); }).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="credit-notes-page">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-heading font-bold text-2xl text-[#1F2937]">Credit Notes</h1><p className="text-sm text-gray-500 mt-1">{total} credit note{total !== 1 ? "s" : ""}</p></div>
        <Button data-testid="create-credit-note-btn" onClick={() => navigate("/credit-notes/new")} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm"><Plus className="w-4 h-4 mr-1.5" />New Credit Note</Button>
      </div>
      <div className="flex gap-2 mb-4">
        {["", "Active", "Fully Applied", "Refunded", "Cancelled"].map(s => (
          <Button key={s} size="sm" variant={filter === s ? "default" : "outline"} onClick={() => setFilter(s)} className={`rounded-sm text-xs ${filter === s ? "bg-[#E63946] text-white" : ""}`}>{s || "All"}</Button>
        ))}
      </div>
      {loading ? <div className="animate-pulse h-40 bg-gray-100 rounded-sm" /> : items.length === 0 ? (
        <Card className="border rounded-sm"><CardContent className="p-12 text-center text-gray-400"><FileText className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>No credit notes found</p></CardContent></Card>
      ) : (
        <Card className="border rounded-sm shadow-sm"><div className="overflow-x-auto">
          <table data-testid="credit-notes-table" className="w-full text-sm">
            <thead><tr className="border-b bg-gray-50/50">
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Number</th>
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Date</th>
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Customer</th>
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Type</th>
              <th className="p-3 text-right text-xs font-semibold uppercase text-gray-500">Total</th>
              <th className="p-3 text-right text-xs font-semibold uppercase text-gray-500">Balance</th>
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Status</th>
            </tr></thead>
            <tbody>{items.map(cn => (
              <tr key={cn.cn_id} data-testid={`cn-row-${cn.cn_id}`} className="border-b border-gray-100 hover:bg-gray-50/50 cursor-pointer" onClick={() => navigate(`/credit-notes/${cn.cn_id}`)}>
                <td className="p-3 font-medium text-[#E63946]">{cn.credit_note_number}</td>
                <td className="p-3 text-gray-600">{cn.credit_note_date}</td>
                <td className="p-3 text-gray-700">{cn.customer_name}</td>
                <td className="p-3"><Badge className="rounded-full text-xs bg-gray-100 text-gray-600">{cn.credit_type}</Badge></td>
                <td className="p-3 text-right font-mono">{fmt(cn.total_amount)}</td>
                <td className="p-3 text-right font-mono font-medium">{fmt(cn.credit_balance)}</td>
                <td className="p-3"><Badge className={`rounded-full text-xs border ${statusColors[cn.status] || "bg-gray-100"}`}>{cn.status}</Badge></td>
              </tr>
            ))}</tbody>
          </table>
        </div></Card>
      )}
    </div>
  );
}

function CreateCreditNote() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({ customer_id: "", invoice_id: "", credit_type: "Return", reason: "", credit_note_date: new Date().toISOString().split("T")[0], notes: "" });
  const [items, setItems] = useState([{ description: "", quantity: 1, unit_price: 0, vat_rate: 15 }]);
  const [invoices, setInvoices] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/customers?limit=500").then(r => setCustomers(r.data.customers || [])); }, []);
  useEffect(() => {
    if (form.customer_id) api.get(`/customers/${form.customer_id}/outstanding-invoices`).then(r => setInvoices(r.data.invoices || []));
    else setInvoices([]);
  }, [form.customer_id]);

  const calcLine = (item) => { const sub = item.quantity * item.unit_price; return round2(sub + sub * item.vat_rate / 100); };
  const subtotal = items.reduce((s, i) => s + round2(i.quantity * i.unit_price), 0);
  const vatTotal = items.reduce((s, i) => s + round2(i.quantity * i.unit_price * i.vat_rate / 100), 0);
  const total = round2(subtotal + vatTotal);

  const updateItem = (idx, field, val) => { const n = [...items]; n[idx] = { ...n[idx], [field]: field === "description" ? val : parseFloat(val) || 0 }; setItems(n); };
  const addItem = () => setItems([...items, { description: "", quantity: 1, unit_price: 0, vat_rate: 15 }]);
  const removeItem = (idx) => { if (items.length > 1) setItems(items.filter((_, i) => i !== idx)); };

  const submit = async () => {
    if (!form.customer_id) { toast.error("Select a customer"); return; }
    if (!form.reason) { toast.error("Enter a reason"); return; }
    if (items.some(i => !i.description || i.quantity <= 0 || i.unit_price <= 0)) { toast.error("Fill all item fields"); return; }
    setSaving(true);
    try {
      const r = await api.post("/credit-notes", { ...form, items });
      toast.success(`Credit note ${r.data.credit_note_number} created`);
      navigate(`/credit-notes/${r.data.cn_id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  return (
    <div data-testid="create-credit-note-page">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/credit-notes")} className="rounded-sm"><ArrowLeft className="w-5 h-5" /></Button>
        <h1 className="font-heading font-bold text-2xl text-[#1F2937]">New Credit Note</h1>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div><label className="text-xs font-medium text-gray-500 uppercase mb-1 block">Customer *</label>
          <Select value={form.customer_id} onValueChange={v => setForm({...form, customer_id: v, invoice_id: ""})}>
            <SelectTrigger data-testid="cn-customer-select" className="rounded-sm"><SelectValue placeholder="Select customer..." /></SelectTrigger>
            <SelectContent>{customers.map(c => <SelectItem key={c.customer_id} value={c.customer_id}>{c.company_name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><label className="text-xs font-medium text-gray-500 uppercase mb-1 block">Original Invoice (optional)</label>
          <Select value={form.invoice_id || "none"} onValueChange={v => setForm({...form, invoice_id: v === "none" ? "" : v})}>
            <SelectTrigger className="rounded-sm"><SelectValue placeholder="Not linked" /></SelectTrigger>
            <SelectContent><SelectItem value="none">Not linked to invoice</SelectItem>{invoices.map(i => <SelectItem key={i.invoice_id} value={i.invoice_id}>{i.invoice_number} — Balance: {fmt(i.balance)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><label className="text-xs font-medium text-gray-500 uppercase mb-1 block">Credit Type *</label>
          <Select value={form.credit_type} onValueChange={v => setForm({...form, credit_type: v})}>
            <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{creditTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><label className="text-xs font-medium text-gray-500 uppercase mb-1 block">Date *</label>
          <Input type="date" value={form.credit_note_date} onChange={e => setForm({...form, credit_note_date: e.target.value})} className="rounded-sm" />
        </div>
        <div className="lg:col-span-2"><label className="text-xs font-medium text-gray-500 uppercase mb-1 block">Reason *</label>
          <Input data-testid="cn-reason" value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} placeholder="e.g., Defective product returned" className="rounded-sm" />
        </div>
      </div>
      <Card className="border rounded-sm mb-6"><CardContent className="p-4">
        <div className="flex items-center justify-between mb-3"><h3 className="font-heading font-semibold text-sm text-[#1F2937]">Line Items</h3><Button size="sm" variant="outline" onClick={addItem} className="rounded-sm h-8 text-xs"><Plus className="w-3 h-3 mr-1" />Add Item</Button></div>
        <table className="w-full text-sm">
          <thead><tr className="border-b"><th className="p-2 text-left text-xs text-gray-500">Description</th><th className="p-2 w-20 text-xs text-gray-500">Qty</th><th className="p-2 w-28 text-xs text-gray-500">Unit Price</th><th className="p-2 w-20 text-xs text-gray-500">VAT %</th><th className="p-2 w-28 text-right text-xs text-gray-500">Line Total</th><th className="p-2 w-10"></th></tr></thead>
          <tbody>{items.map((item, idx) => (
            <tr key={idx} className="border-b border-gray-100">
              <td className="p-2"><Input value={item.description} onChange={e => updateItem(idx, "description", e.target.value)} placeholder="Item description" className="rounded-sm h-8 text-sm" /></td>
              <td className="p-2"><Input type="number" value={item.quantity} onChange={e => updateItem(idx, "quantity", e.target.value)} className="rounded-sm h-8 text-sm" min="0" step="0.01" /></td>
              <td className="p-2"><Input type="number" value={item.unit_price} onChange={e => updateItem(idx, "unit_price", e.target.value)} className="rounded-sm h-8 text-sm" min="0" step="0.01" /></td>
              <td className="p-2"><Input type="number" value={item.vat_rate} onChange={e => updateItem(idx, "vat_rate", e.target.value)} className="rounded-sm h-8 text-sm" min="0" step="0.01" /></td>
              <td className="p-2 text-right font-mono">{fmt(calcLine(item))}</td>
              <td className="p-2"><Button variant="ghost" size="icon" onClick={() => removeItem(idx)} className="h-7 w-7 text-gray-400 hover:text-red-500" disabled={items.length <= 1}><Trash2 className="w-3.5 h-3.5" /></Button></td>
            </tr>
          ))}</tbody>
          <tfoot>
            <tr className="border-t"><td colSpan="4" className="p-2 text-right text-xs text-gray-500">Subtotal:</td><td className="p-2 text-right font-mono">{fmt(subtotal)}</td><td></td></tr>
            <tr><td colSpan="4" className="p-2 text-right text-xs text-gray-500">VAT:</td><td className="p-2 text-right font-mono">{fmt(vatTotal)}</td><td></td></tr>
            <tr className="font-bold"><td colSpan="4" className="p-2 text-right">Total Credit:</td><td className="p-2 text-right font-mono text-lg text-[#E63946]">{fmt(total)}</td><td></td></tr>
          </tfoot>
        </table>
      </CardContent></Card>
      <div><label className="text-xs font-medium text-gray-500 uppercase mb-1 block">Notes</label>
        <textarea className="w-full border rounded-sm p-2 text-sm h-20 resize-none" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Internal notes" />
      </div>
      <div className="flex gap-3 mt-6">
        <Button data-testid="save-credit-note-btn" onClick={submit} disabled={saving} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">{saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />}Create Credit Note</Button>
        <Button variant="outline" onClick={() => navigate("/credit-notes")} className="rounded-sm">Cancel</Button>
      </div>
    </div>
  );
}

function CreditNoteDetail({ cnId }) {
  const navigate = useNavigate();
  const [cn, setCn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applyDialog, setApplyDialog] = useState(false);
  const [refundDialog, setRefundDialog] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [applyForm, setApplyForm] = useState({ invoice_id: "", amount: 0, notes: "" });
  const [refundForm, setRefundForm] = useState({ refund_amount: 0, refund_method: "EFT", bank_account: "", reference: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/credit-notes/${cnId}`).then(r => {
      setCn(r.data);
      if (r.data.customer_id) api.get(`/customers/${r.data.customer_id}/outstanding-invoices`).then(r2 => setInvoices(r2.data.invoices || []));
    }).catch(() => toast.error("Not found")).finally(() => setLoading(false));
  }, [cnId]);
  useEffect(() => { load(); }, [load]);

  const applyCredit = async () => {
    if (!applyForm.invoice_id || applyForm.amount <= 0) { toast.error("Select invoice and amount"); return; }
    setSubmitting(true);
    try {
      await api.post(`/credit-notes/${cnId}/apply`, applyForm);
      toast.success("Credit applied to invoice");
      setApplyDialog(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSubmitting(false);
  };

  const processRefund = async () => {
    if (!refundForm.refund_method || refundForm.refund_amount <= 0) { toast.error("Fill required fields"); return; }
    setSubmitting(true);
    try {
      const r = await api.post(`/credit-notes/${cnId}/refund`, refundForm);
      toast.success(`Refund ${r.data.refund_number} submitted for approval`);
      setRefundDialog(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSubmitting(false);
  };

  const cancelCN = async () => {
    if (!window.confirm("Cancel this credit note?")) return;
    try { await api.post(`/credit-notes/${cnId}/cancel`); toast.success("Credit note cancelled"); load(); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const downloadPDF = () => {
    window.open(`${api.defaults.baseURL}/credit-notes/${cnId}/pdf`, "_blank");
  };

  const emailCN = async () => {
    setSubmitting(true);
    try {
      const r = await api.post(`/credit-notes/${cnId}/email`);
      if (r.data.success) toast.success(r.data.message);
      else toast.error(r.data.error || "Email failed");
    } catch (err) { toast.error(err.response?.data?.detail || "Email failed"); }
    setSubmitting(false);
  };

  if (loading) return <div className="animate-pulse h-60 bg-gray-100 rounded-sm" />;
  if (!cn) return <p>Not found</p>;

  return (
    <div data-testid="credit-note-detail">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/credit-notes")} className="rounded-sm"><ArrowLeft className="w-5 h-5" /></Button>
        <div className="flex-1"><h1 className="font-heading font-bold text-2xl text-[#1F2937]">{cn.credit_note_number}</h1><p className="text-sm text-gray-500">{cn.customer_name}</p></div>
        <Badge className={`rounded-full text-sm border px-3 py-1 ${statusColors[cn.status] || "bg-gray-100"}`}>{cn.status}</Badge>
      </div>
      {cn.status !== "Cancelled" && (
        <div className="flex gap-2 mb-6">
          <Button data-testid="download-cn-pdf" size="sm" variant="outline" onClick={downloadPDF} className="rounded-sm"><Download className="w-4 h-4 mr-1.5" />Download PDF</Button>
          <Button data-testid="email-cn-btn" size="sm" variant="outline" onClick={emailCN} disabled={submitting} className="rounded-sm"><Mail className="w-4 h-4 mr-1.5" />Email to Customer</Button>
          {cn.status === "Active" && (
            <>
              <Button data-testid="apply-credit-btn" size="sm" onClick={() => { setApplyForm({ invoice_id: "", amount: cn.credit_balance, notes: "" }); setApplyDialog(true); }} className="bg-green-600 hover:bg-green-700 text-white rounded-sm"><CreditCard className="w-4 h-4 mr-1.5" />Apply to Invoice</Button>
              <Button data-testid="process-refund-btn" size="sm" onClick={() => { setRefundForm({ refund_amount: cn.credit_balance, refund_method: "EFT", bank_account: "", reference: "", notes: "" }); setRefundDialog(true); }} className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm"><RefreshCw className="w-4 h-4 mr-1.5" />Process Refund</Button>
              <Button size="sm" variant="outline" onClick={cancelCN} className="rounded-sm text-red-500 border-red-300 hover:bg-red-50"><Ban className="w-4 h-4 mr-1.5" />Cancel</Button>
            </>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Card className="border rounded-sm"><CardContent className="p-4 space-y-2">
          <p className="text-xs text-gray-500 uppercase font-semibold">Details</p>
          <p className="text-sm"><span className="text-gray-500">Date:</span> {cn.credit_note_date}</p>
          <p className="text-sm"><span className="text-gray-500">Type:</span> {cn.credit_type}</p>
          <p className="text-sm"><span className="text-gray-500">Reason:</span> {cn.reason}</p>
          {cn.invoice_id && <p className="text-sm"><span className="text-gray-500">Invoice:</span> <span className="text-[#E63946] cursor-pointer" onClick={() => navigate(`/invoices/${cn.invoice_id}`)}>{cn.invoice_id}</span></p>}
          {cn.notes && <p className="text-sm text-gray-500 italic">{cn.notes}</p>}
        </CardContent></Card>
        <Card className="border rounded-sm"><CardContent className="p-4 text-center">
          <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Total Credit</p>
          <p className="text-3xl font-bold text-[#1F2937] font-mono">{fmt(cn.total_amount)}</p>
          <p className="text-xs text-gray-400 mt-1">Subtotal: {fmt(cn.subtotal)} + VAT: {fmt(cn.vat_amount)}</p>
        </CardContent></Card>
        <Card className="border rounded-sm border-green-200"><CardContent className="p-4 text-center">
          <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Available Balance</p>
          <p className={`text-3xl font-bold font-mono ${cn.credit_balance > 0 ? "text-green-600" : "text-gray-400"}`}>{fmt(cn.credit_balance)}</p>
          <p className="text-xs text-gray-400 mt-1">{cn.credit_balance > 0 ? "Available for allocation" : "Fully used"}</p>
        </CardContent></Card>
      </div>
      <Card className="border rounded-sm mb-6"><CardContent className="p-4">
        <h3 className="font-heading font-semibold text-sm mb-3">Items</h3>
        <table className="w-full text-sm"><thead><tr className="border-b"><th className="p-2 text-left text-xs text-gray-500">Description</th><th className="p-2 text-xs text-gray-500">Qty</th><th className="p-2 text-right text-xs text-gray-500">Unit Price</th><th className="p-2 text-xs text-gray-500">VAT %</th><th className="p-2 text-right text-xs text-gray-500">Total</th></tr></thead>
          <tbody>{(cn.items || []).map(i => <tr key={i.item_id} className="border-b border-gray-100"><td className="p-2">{i.description}</td><td className="p-2 text-center">{i.quantity}</td><td className="p-2 text-right font-mono">{fmt(i.unit_price)}</td><td className="p-2 text-center">{i.vat_rate}%</td><td className="p-2 text-right font-mono">{fmt(i.line_total)}</td></tr>)}</tbody>
        </table>
      </CardContent></Card>
      {cn.applications && cn.applications.length > 0 && (
        <Card className="border rounded-sm mb-6"><CardContent className="p-4">
          <h3 className="font-heading font-semibold text-sm mb-3">Applications</h3>
          <table className="w-full text-sm"><thead><tr className="border-b"><th className="p-2 text-left text-xs text-gray-500">Date</th><th className="p-2 text-left text-xs text-gray-500">Invoice</th><th className="p-2 text-right text-xs text-gray-500">Amount</th><th className="p-2 text-left text-xs text-gray-500">Applied By</th></tr></thead>
            <tbody>{cn.applications.map(a => <tr key={a.application_id} className="border-b border-gray-100"><td className="p-2">{a.applied_date}</td><td className="p-2 text-[#E63946] cursor-pointer" onClick={() => navigate(`/invoices/${a.invoice_id}`)}>{a.invoice_number}</td><td className="p-2 text-right font-mono">{fmt(a.amount_applied)}</td><td className="p-2 text-gray-500">{a.applied_by_name}</td></tr>)}</tbody>
          </table>
        </CardContent></Card>
      )}
      {/* Apply Credit Dialog */}
      <Dialog open={applyDialog} onOpenChange={setApplyDialog}>
        <DialogContent className="rounded-sm"><DialogHeader><DialogTitle>Apply Credit to Invoice</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-500 mb-3">Available credit: <strong>{fmt(cn.credit_balance)}</strong></p>
          <div className="space-y-3">
            <div><label className="text-xs font-medium text-gray-500 mb-1 block">Invoice *</label>
              <Select value={applyForm.invoice_id || "none"} onValueChange={v => { const inv = invoices.find(i => i.invoice_id === v); setApplyForm({...applyForm, invoice_id: v === "none" ? "" : v, amount: inv ? Math.min(cn.credit_balance, inv.balance) : 0}); }}>
                <SelectTrigger className="rounded-sm"><SelectValue placeholder="Select invoice..." /></SelectTrigger>
                <SelectContent>{invoices.map(i => <SelectItem key={i.invoice_id} value={i.invoice_id}>{i.invoice_number} — Balance: {fmt(i.balance)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-xs font-medium text-gray-500 mb-1 block">Amount *</label><Input type="number" value={applyForm.amount} onChange={e => setApplyForm({...applyForm, amount: parseFloat(e.target.value) || 0})} className="rounded-sm" step="0.01" min="0" max={cn.credit_balance} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setApplyDialog(false)} className="rounded-sm">Cancel</Button><Button onClick={applyCredit} disabled={submitting} className="bg-green-600 hover:bg-green-700 text-white rounded-sm">{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply Credit"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Refund Dialog */}
      <Dialog open={refundDialog} onOpenChange={setRefundDialog}>
        <DialogContent className="rounded-sm"><DialogHeader><DialogTitle>Process Refund</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-500 mb-3">Available: <strong>{fmt(cn.credit_balance)}</strong></p>
          <div className="space-y-3">
            <div><label className="text-xs font-medium text-gray-500 mb-1 block">Amount *</label><Input type="number" value={refundForm.refund_amount} onChange={e => setRefundForm({...refundForm, refund_amount: parseFloat(e.target.value) || 0})} className="rounded-sm" step="0.01" min="0" max={cn.credit_balance} /></div>
            <div><label className="text-xs font-medium text-gray-500 mb-1 block">Method *</label>
              <Select value={refundForm.refund_method} onValueChange={v => setRefundForm({...refundForm, refund_method: v})}>
                <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{["EFT", "Cash", "Card", "Cheque"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {refundForm.refund_method === "EFT" && <div><label className="text-xs font-medium text-gray-500 mb-1 block">Bank Details</label><textarea className="w-full border rounded-sm p-2 text-sm h-16 resize-none" value={refundForm.bank_account} onChange={e => setRefundForm({...refundForm, bank_account: e.target.value})} placeholder="Bank Name, Account Number, Branch Code" /></div>}
            <div><label className="text-xs font-medium text-gray-500 mb-1 block">Reference</label><Input value={refundForm.reference} onChange={e => setRefundForm({...refundForm, reference: e.target.value})} className="rounded-sm" /></div>
          </div>
          <p className="text-xs text-amber-600 mt-2">This refund will require admin approval before payment.</p>
          <DialogFooter><Button variant="outline" onClick={() => setRefundDialog(false)} className="rounded-sm">Cancel</Button><Button onClick={processRefund} disabled={submitting} className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm">{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit for Approval"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function round2(v) { return Math.round(v * 100) / 100; }
